import { FullResourceFragment } from "~/graphql/codegen/graphql";
import { useAnnotatePopoverConfiguration } from "~/components/annotate/hooks/use-annotate-popover-configuration";
import useAnnotationManager from "./hooks/annotation-manager";
import React, { useEffect, useRef, useState } from "react";
import { useProgressReporterScrollListener } from "~/components/annotate/hooks/use-progress-repoter";
import metrics from "~/utils/metrics";
import AnnotateTriviaBar from "~/components/annotate/annotate-trivia-bar";
import { ArrowUpIcon } from "@heroicons/react/20/solid";
import { useScroll } from "framer-motion";
import { HTMLAnnotator } from "./html-annotator";
import {
  IHtmlHighlight,
  IResourceComment,
} from "~/typings/annotate/annotation";
import { classNames } from "~/utils";

export function AnnotateViewHtml({
  article,
}: {
  article: FullResourceFragment;
}) {
  const popoverConfiguration = useAnnotatePopoverConfiguration(article);
  const [comments, highlights] = useAnnotationManager(
    article.id,
    article.annotations,
  );
  const [scrollToTopVisible, setScrollToTopVisible] = useState(false);

  const transcriptRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ container: transcriptRef });
  useProgressReporterScrollListener(
    transcriptRef as React.RefObject<HTMLDivElement>,
  );
  useEffect(() => {
    if (!scrollYProgress || !transcriptRef.current) {
      return;
    }
    scrollYProgress.on("change", (p) => {
      if (p < 0.01) {
        p = 0;
      } else if (p > 0.99) {
        p = 1;
      }
      setScrollToTopVisible(p > 0.01);
    });
    return () => {
      scrollYProgress.clearListeners();
    };
  }, [scrollYProgress]);
  return (
    <div
      className={
        "h-full w-full flex flex-col overflow-auto px-12 py-12 space-y-12"
      }
      ref={transcriptRef}
    >
      <h2 className="mx-auto text-justify max-w-3xl">{article.headline}</h2>
      <div className="mx-auto w-full max-w-3xl flex flex-col space-y-6">
        <HTMLAnnotator
          annotations={{
            highlights: highlights as IHtmlHighlight[],
            comments: comments as IResourceComment[],
          }}
          editorContent={article.editorContent ?? ""}
          popoverConfiguration={popoverConfiguration}
          trackMetric={metrics.track}
          articleID={article.id}
        />
        <AnnotateTriviaBar resource={article} />

        {scrollToTopVisible && (
          <div
            className={classNames(
              "fixed flex flex-col-reverse  justify-end z-5 max-h-0 right-6 " +
                " transition-all bottom-[150px] drop-shadow-md",
            )}
          >
            <div className="text-end mb-4 md:mb-0 h-auto">
              <button
                className="hover:[&>svg]:bg-muted/80"
                onClick={() => {
                  transcriptRef.current?.scrollTo({
                    top: 0,
                    behavior: "smooth",
                  });
                }}
                title="Scroll To Top"
              >
                <ArrowUpIcon className="w-10 h-10 p-1.5 text-foreground bg-muted rounded-full" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
